import { useRef, useState } from 'react'

import { useNavigate, useSearch } from '@tanstack/react-router'

import Badge from '@ingka/badge'
import ListBox, { MenuItem } from '@ingka/list-box'
import Pill from '@ingka/pill'
import chevronDown from '@ingka/ssr-icon/paths/chevron-down-small'
import widthIcon from '@ingka/ssr-icon/paths/width'

import { dataTestIds } from '@/constants/selectors'
import type { StartGalleryFilter } from '@/features/start-gallery'
import { useClickOutside } from '@/hooks/use-click-outside'
import { useFocusOut } from '@/hooks/use-focus-out'
import { useInsights } from '@/hooks/use-insights'
import { useSettingsAndTranslations } from '@/hooks/use-settings-and-translations'
import { useTranslate } from '@/hooks/use-translate'

export function SizeFilter() {
  const [isOpen, setIsOpen] = useState(false)
  const { sendCustomEvent } = useInsights()

  const { width } = useSearch({
    from: '/$country/$language/$mode/',
  })
  const navigate = useNavigate({ from: '/$country/$language/$mode/' })
  const { translateString } = useTranslate()
  const { dexfSettings, translations } = useSettingsAndTranslations()
  const ref = useRef<HTMLDivElement | null>(null)

  useClickOutside(ref, () => {
    setIsOpen(false)
  })

  useFocusOut(ref, () => {
    setIsOpen(false)
  })

  const measurementSymbol = translations.kompis.measureValue
  const measurementSystem = dexfSettings.localisation.useMetricMeasures ? 'metric' : 'imperial'

  const widthLabels = {
    large: { imperial: `> 120 ${measurementSymbol}`, metric: '> 3m  ' },
    medium: { imperial: `80 ${measurementSymbol} < 120 ${measurementSymbol}`, metric: '2m < 3m' },
    small: { imperial: `< 80 ${measurementSymbol}`, metric: '< 2m' },
  }

  return (
    <ListBox
      containerRef={ref as React.RefObject<HTMLDivElement>}
      onChange={(selectedItems) => {
        if (!selectedItems.length) {
          return
        }

        const [selectedWidth] = selectedItems as StartGalleryFilter['width'][]
        if (selectedWidth === width) {
          return
        }

        void navigate({ search: (prev) => ({ ...prev, width: selectedWidth }) })
        void sendCustomEvent('filter_width', { payload: { width: selectedWidth } })

        setIsOpen(false)
      }}
      open={isOpen}
      position="left"
      trigger={
        <Pill
          data-testid={dataTestIds.landing.widthPill}
          label={
            <>
              {translateString('startpage.filter.width')}
              {width !== 'all' && <Badge colour="black" label={width.toUpperCase()} size="small" />}
            </>
          }
          leadingIcon={widthIcon}
          onClick={() => {
            setIsOpen((prev) => !prev)
          }}
          selected={width !== 'all'}
          size="small"
          trailingIcon={chevronDown}
        />
      }
      value={[width]}
    >
      <MenuItem id="all" title={translateString('startpage.filter.width_options.all')} />
      <MenuItem
        id="s"
        quantityLabel={widthLabels.small[measurementSystem]}
        title={translateString('startpage.filter.width_options.small')}
      />
      <MenuItem
        id="m"
        quantityLabel={widthLabels.medium[measurementSystem]}
        title={translateString('startpage.filter.width_options.medium')}
      />
      <MenuItem
        id="l"
        quantityLabel={widthLabels.large[measurementSystem]}
        title={translateString('startpage.filter.width_options.large')}
      />
    </ListBox>
  )
}
